import React from 'react';

import { AwsRum, AwsRumConfig, DomEventPluginConfig, TelemetryEnum } from 'aws-rum-web';
import { computeAccessibleName } from 'dom-accessibility-api';

import { ClickActionElementSelector } from '@wildidea/components/utils/ClickActionElement';
import { AnyFormAssociatedElementSelector } from '@wildidea/components/utils/FormAssociatedElement';

const setupRUM = (): AwsRum | null => {
  if (window.self !== window.top) return null;

  const [APPLICATION_REGION, APPLICATION_ID] = process.env.APP_MONITOR_ID?.split(':') || [];
  const RESCUEALLY_WEBSITE_VERSION = process.env.RESCUEALLY_WEBSITE_VERSION!;

  const config = {
    sessionSampleRate: 1,
    identityPoolId: process.env.IDENTITY_POOL_ID,
    endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
    telemetries: [
      TelemetryEnum.Errors,
      TelemetryEnum.Http,
      [
        TelemetryEnum.Interaction,
        {
          events: [
            { event: 'click', cssLocator: ClickActionElementSelector },
            { event: 'focus', cssLocator: AnyFormAssociatedElementSelector }
          ],
          enableMutationObserver: true,
          interactionId: (event) => {
            const closestInteractive =
              (event.target as HTMLElement)?.closest(
                `${ClickActionElementSelector},${AnyFormAssociatedElementSelector}`
              ) || null;
            return closestInteractive ? computeAccessibleName(closestInteractive) : '[unknown]';
          }
        } satisfies DomEventPluginConfig
      ]
    ],
    allowCookies: true,
    enableXRay: false,
    signing: true,
    sessionAttributes: {}
  } satisfies AwsRumConfig;

  const rum = new AwsRum(APPLICATION_ID, RESCUEALLY_WEBSITE_VERSION, APPLICATION_REGION, config);

  return rum;
};

const rum = setupRUM();

export const recordCapacitorEvent = (event: { type: string; detail?: object }) => {
  rum?.recordEvent(`com.capacitorjs.${event.type}`, event.detail || {});
};

export const recordWildideaEvent = (event: { type: string; detail?: object }) => {
  rum?.recordEvent(`io.wildidea.${event.type}`, event.detail || {});
};

interface StateSourcedRUMAttributes {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export const RUMStateListener: React.FC<StateSourcedRUMAttributes> = (
  attributes: StateSourcedRUMAttributes
) => {
  const { utmSource, utmMedium, utmCampaign } = attributes;
  React.useEffect(
    function updateRUMConfigWithChangedAttributes() {
      rum?.addSessionAttributes(
        Object.fromEntries(Object.entries(attributes).filter(([_key, value]) => !!value))
      );
    },
    [utmSource, utmMedium, utmCampaign]
  );

  return null;
};
